import { Box, Card, Grid, Stack, Typography, IconButton, Chip, Divider } from "@mui/material";
// import MDButton from "components/MDButton";
import CommonLayout from "layouts/DashboardLayout";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import AngleRightIcon from "common/customIcons/angle_right";
import { useDispatch } from "react-redux";
import { openSnackbar } from "../../redux/slices/common";
import { getPromotionData } from "../../redux/slices/promotions";
import PromotionRuleFormattedText from "./PromotionRuleFormattedText";
import dayjs from "dayjs";
import Loader from "common/Loader";
import ActiveIcon from "common/customIcons/active";
import InActiveIcon from "common/customIcons/inactive";
import MDButton from "components/MDButton";
import EditIcon from "common/customIcons/edit";

const PromoDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { promoId } = useParams();

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAllPromoCodes, setShowAllPromoCodes] = useState(false);

  const showResponseMessage = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };
  const formatDateForDisplay = (dateValue) => {
    if (!dateValue) return "-";

    try {
      // If it's already a formatted string "YYYY-MM-DD HH:mm:ss", use it directly
      if (
        typeof dateValue === "string" &&
        /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateValue)
      ) {
        // Parse as local date and format for display
        const date = dayjs(dateValue, "YYYY-MM-DD HH:mm:ss");
        return date.format("DD MMM, YYYY hh:mm A");
      }

      // If it's a UTC Date string (ISO format), parse it and treat as local
      const date = dayjs(dateValue);
      if (!date.isValid()) return "-";

      // Format for display - this will show the date as entered, not converted
      return date.format("DD MMM, YYYY hh:mm A");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };
  const promoCodeText = formData.code?.join(", ") || "-";
  const shouldShowPromoCodeToggle = promoCodeText !== "-" && promoCodeText.length > 320;
  const shortDescriptionText = formData.description || "-";
  const shouldShowDescriptionToggle = shortDescriptionText !== "-" && shortDescriptionText.length > 320;

  useEffect(() => {
    if (!promoId) return;

    setLoading(true);
    dispatch(getPromotionData(promoId))
      .unwrap()
      .then((res) => {
        const promo = res?.data;
        setFormData({
          ...promo,
          rules: {
            ...promo?.rules,
            creteria: promo?.rules?.creteria || "",
          },
        });
        setLoading(false);
      })
      .catch((err) => {
        showResponseMessage(err?.message || "Failed to load promotion", "error");
        setLoading(false);
      });
  }, [promoId, dispatch]);

  if (loading) {
    return (
      <CommonLayout>
        <Loader />
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} className="add-promotion-container">
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Promotion Dashboard
            </Typography>
          </Box>

          <Box display="flex" alignItems="center">
            {formData.platform && (
              <MDButton
                               type='button'
                               variant='outlined'
                               color='dark'
                               size='small'
                               startIcon={<EditIcon width='15px' height='15px' />}
                               onClick={() => navigate(`/promotions/adds/${promoId}`)}
                             >
                               Edit
                             </MDButton>
            )}
          </Box>
        </Stack>

        <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none" }}>
          {/* Title and Status */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            gap={2}
            mb={4.5}
          >
            <Typography variant="h4" fontWeight="semibold" color="secondary">
              Promotion Details
            </Typography>
            <Box display="flex" alignItems="center" gap={1.25}>
              {formData?.status === "active" ? (
                <Box color="#24CA49" display="flex" alignItems="center">
                  <ActiveIcon width="16px" height="16px" flex="none" />
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    ml={0.5}
                    textTransform="capitalize"
                  >
                    Active
                  </Typography>
                </Box>
              ) : (
                <Box color="#FD4438" display="flex" alignItems="center">
                  <InActiveIcon width="16px" height="16px" flex="none" />
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    ml={0.5}
                    textTransform="capitalize"
                  >
                    Inactive
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>

          {/* Basic Info */}
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6} lg={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Promotion Name
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formData.name || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} xl={3}>
              <Typography variant="subtitle2" color="textSecondary">
                Promo Code Count
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formData.code?.length ? formData.code.length : "0"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Promo Code
              </Typography>
              <Typography
                variant="body1"
                fontWeight={500}
                sx={{
                  wordBreak: "break-all",
                  overflow: "hidden",
                  display: showAllPromoCodes ? "block" : "-webkit-box",
                  WebkitLineClamp: showAllPromoCodes ? "unset" : 8,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {promoCodeText}
              </Typography>
              {shouldShowPromoCodeToggle && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.75,
                    display: "inline-block",
                    color: "primary.main",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  onClick={() => setShowAllPromoCodes((prev) => !prev)}
                >
                  {showAllPromoCodes ? "View less" : "Read more"}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Platform
              </Typography>
              <Typography variant="body1" fontWeight={500}>
              {formData.platform.charAt(0).toUpperCase() + formData.platform.slice(1)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={12} lg={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Short Description
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formData.description || "-"}
              </Typography>
            </Grid>

            {/* Divider */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1, borderColor: "#F2F3F7" }} />
            </Grid>

            {/* Rules */}
            <Grid item xs={12}>
              <Typography variant="h5" fontWeight="bold" color="#2E2E3A" mb={2.5}>
                Promotion Rules & Settings
              </Typography>
              <Typography variant="h6" fontWeight="600" color="#2E2E3A" mb={1.5}>
                Promotion Rules
              </Typography>

              {formData?.rules && Object.keys(formData.rules).length > 0 ? (
                <PromotionRuleFormattedText
                  ruleSet={formData.rules}
                  allCategories={[]} // Passing empty array as per instruction
                  userGroups={[]}
                  isActionsAllow={false}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No rules defined.
                </Typography>
              )}
            </Grid>

            {/* Divider */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1, borderColor: "#F2F3F7" }} />
            </Grid>

            {/* Settings */}
            <Grid item xs={12}>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1">Auto Apply:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formData.autoApply || formData.rules?.autoApply ? "Yes" : "No"}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1">Publish Date:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {/* {formData.publishDate ?  */}

                    {formatDateForDisplay(formData?.publishDate)}
                    {/* //  dayjs(formData.publishDate).format("YYYY-MM-DD HH:mm:ss") : "Immediately"} */}
                  </Typography>
                </Box>

                {formData.isExpiryDateRequired && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1">Expiry Date:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formData.expiryDate ? formatDateForDisplay(formData?.expiryDate) : "-"}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Grid>

            {/* Divider */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1, borderColor: "#F2F3F7" }} />
            </Grid>

            {/* Restrictions */}
            <Grid item xs={12}>
              <Typography variant="h5" fontWeight="bold" color="#2E2E3A" mb={2.5}>
                Restrictions
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="body2" fontWeight="600" color="#2E2E3A">
                    Individual Use Only
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formData.isIndividualUsed ? "Yes" : "No"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="600" color="#2E2E3A">
                    Exclude Sale Items
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formData.allowSalesItem ? "Yes" : "No"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Divider */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1, borderColor: "#F2F3F7" }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5" fontWeight="bold" color="#2E2E3A" mb={2.5}>
                Usage Limits
              </Typography>

              <Grid container spacing={3}>
                {/* Usage Limit */}
                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid #F2F3F7",
                      borderRadius: 2,
                      backgroundColor: "#FAFAFC",
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
                      Usage Limit Per Promotion
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      {formData.usageLimit ?? "-"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Usage Limit Per User */}
                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid #F2F3F7",
                      borderRadius: 2,
                      backgroundColor: "#FAFAFC",
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
                      Usage Limit Per User
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      {formData.usageLimitPerUser ?? "-"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </Box>
    </CommonLayout>
  );
};

export default PromoDetails;
